import React, { useState } from 'react';
import { Lightbulb, Target, Swords, Wrench, Sparkles, Save, Copy, ChevronRight, TrendingUp, AlertTriangle } from 'lucide-react';
import { scoreIdea, estimateBuildComplexity, generateCompetitorAnalysis, type IdeaResult } from './lib/engine';
import { aiDeepAnalysis } from './lib/ai-fallback';
import { SettingsButton } from './lib/SettingsButton';

interface SavedIdea { id: string; description: string; result: IdeaResult; build: ReturnType<typeof estimateBuildComplexity>; competitors: ReturnType<typeof generateCompetitorAnalysis>; date: string; }

export default function App() {
  const [description, setDescription] = useState('');
  const [audience, setAudience] = useState('Freelancers');
  const [monetization, setMonetization] = useState('One-time');
  const [skills, setSkills] = useState<string[]>([]);
  const [buildTime, setBuildTime] = useState('2 weeks');
  const [result, setResult] = useState<IdeaResult | null>(null);
  const [build, setBuild] = useState<ReturnType<typeof estimateBuildComplexity> | null>(null);
  const [competitors, setCompetitors] = useState<ReturnType<typeof generateCompetitorAnalysis> | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [saved, setSaved] = useState<SavedIdea[]>(() => JSON.parse(localStorage.getItem('ca_ideas') || '[]'));
  const [showCompare, setShowCompare] = useState(false);

  const skillOptions = ['Frontend', 'Backend', 'AI/ML', 'DevOps', 'Design', 'Marketing'];

  const autoDetectCategory = (text: string) => {
    const t = text.toLowerCase();
    if (/\b(productivity|task|todo|calendar|schedule|time)\b/.test(t)) return 'productivity';
    if (/\b(ai|gpt|llm|prompt|automation|smart|generate)\b/.test(t)) return 'ai_tools';
    if (/\b(finance|invoice|budget|accounting|money|tax)\b/.test(t)) return 'finance';
    if (/\b(marketing|seo|email|social|content|analytics)\b/.test(t)) return 'marketing';
    if (/\b(code|dev|api|deploy|git|server)\b/.test(t)) return 'development';
    return 'productivity';
  };

  const validate = () => {
    if (!description.trim()) return;
    const answers = {
      problem_severity: /\b(waste|struggle|frustrated|pain|hate|annoying|terrible)\b/i.test(description) ? 'critical_pain' : /\b(nice|consider|maybe|would be good)\b/i.test(description) ? 'minor_pain' : 'significant_pain',
      market_size: audience === 'Consumers' ? 'mass_market' : audience === 'Small Business' ? 'large_niche' : audience === 'Freelancers' ? 'small_niche' : 'micro_niche',
      competition_density: /\b(unique|first|only|no one|nobody)\b/i.test(description) ? 'blue_ocean' : /\b(competitor|existing|already|similar)\b/i.test(description) ? 'crowded' : 'few_competitors',
      build_simplicity: buildTime === 'Weekend' ? 'weekend_project' : buildTime === '2 weeks' ? 'month_project' : buildTime === '1 month' ? 'quarter_project' : buildTime === '3 months' ? 'complex_product' : 'enterprise_grade',
      monetization_clarity: monetization === 'One-time' ? 'clear_one_time' : monetization === 'Subscription' ? 'clear_subscription' : monetization === 'Freemium' ? 'freemium_viable' : monetization === 'Ads' ? 'unclear' : 'no_monetization'
    };
    const res = scoreIdea(answers);
    const b = estimateBuildComplexity(description, skills);
    const c = generateCompetitorAnalysis(autoDetectCategory(description));
    setResult(res);
    setBuild(b);
    setCompetitors(c);
    setAiAnalysis('');
  };

  const handleAiDeep = async () => {
    if (!result) return;
    setIsAiLoading(true); setAiAnalysis('');
    await aiDeepAnalysis(description, result.dimensions, c => setAiAnalysis(p => p + c));
    setIsAiLoading(false);
  };

  const saveIdea = () => {
    if (!result || !build) return;
    const idea: SavedIdea = { id: Date.now().toString(), description, result, build, competitors: competitors!, date: new Date().toLocaleDateString() };
    const n = [idea, ...saved]; setSaved(n); localStorage.setItem('ca_ideas', JSON.stringify(n));
  };

  const verdictColor = (v: string) => v === 'STRONG BUILD' ? 'text-green-400' : v === 'PROMISING' ? 'text-yellow-400' : v === 'RISKY' ? 'text-orange-400' : 'text-red-400';
  const verdictBg = (v: string) => v === 'STRONG BUILD' ? 'bg-green-500/10 border-green-500/30' : v === 'PROMISING' ? 'bg-yellow-500/10 border-yellow-500/30' : v === 'RISKY' ? 'bg-orange-500/10 border-orange-500/30' : 'bg-red-500/10 border-red-500/30';

  return (
    <div className="min-h-screen bg-[#050b14] text-[#f0f0f0]">
      <header className="border-b border-white/10 bg-[#0a1628] sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#d4af37] flex items-center justify-center text-[#0a1628] font-bold text-lg">C</div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-[#f0f0f0]">CatalystAgile <span className="text-[#d4af37]">Startup Idea Validator</span></h1>
              <p className="text-xs text-[#8a9bb8]">5-Dimension Scoring + Build Estimator + AI Deep Analysis</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowCompare(!showCompare)} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 text-[#8a9bb8] transition-colors">{showCompare ? 'Hide Compare' : 'Compare Ideas'}</button>
            {result && <button onClick={saveIdea} className="px-3 py-1.5 text-xs rounded-md bg-[#d4af37] text-[#0a1628] font-semibold hover:bg-[#e8c547] transition-colors flex items-center gap-1.5"><Save className="w-3.5 h-3.5"/> Save</button>}
            <SettingsButton className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 inline-flex items-center gap-1.5 transition-colors text-[#8a9bb8] hover:text-[#d4af37]" />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {showCompare && saved.length > 0 && (
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4 mb-6">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-3">Idea Comparison</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-[#8a9bb8] border-b border-white/10"><th className="text-left py-2 px-3">Idea</th><th className="text-center py-2 px-3">Score</th><th className="text-center py-2 px-3">Verdict</th><th className="text-center py-2 px-3">Build Weeks</th><th className="text-center py-2 px-3">Skill Match</th></tr></thead>
                <tbody>
                  {saved.slice(0, 5).map(idea => (
                    <tr key={idea.id} className="border-b border-white/5 hover:bg-[#132238]/50">
                      <td className="py-2 px-3 text-[#f0f0f0] max-w-[200px] truncate">{idea.description.slice(0, 60)}...</td>
                      <td className="py-2 px-3 text-center font-bold">{idea.result.overallScore}</td>
                      <td className="py-2 px-3 text-center"><span className={`px-2 py-0.5 rounded text-[10px] font-medium ${verdictBg(idea.result.verdict)} ${verdictColor(idea.result.verdict)}`}>{idea.result.verdict}</span></td>
                      <td className="py-2 px-3 text-center text-[#8a9bb8]">{idea.build.estimatedWeeks}w</td>
                      <td className="py-2 px-3 text-center text-[#8a9bb8]">{idea.build.skillMatch}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <section className="lg:col-span-5 space-y-4">
            <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
              <h2 className="text-sm font-semibold text-[#d4af37] mb-4 flex items-center gap-2"><Lightbulb className="w-4 h-4"/> Describe Your Idea</h2>
              <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4}
                placeholder="Describe your app idea in 1-2 sentences..."
                className="w-full bg-[#070f1a] border border-white/10 rounded-lg p-3 text-sm text-[#f0f0f0] placeholder-[#8a9bb8]/50 focus:outline-none focus:border-[#d4af37]/50 resize-none mb-4"/>
              <div className="space-y-3">
                <div><label className="text-[10px] uppercase tracking-wider text-[#8a9bb8] mb-1 block">Target Audience</label>
                  <select value={audience} onChange={e => setAudience(e.target.value)} className="w-full bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#d4af37]/50">
                    <option>Freelancers</option><option>Small Business</option><option>Enterprise</option><option>Developers</option><option>Consumers</option>
                  </select>
                </div>
                <div><label className="text-[10px] uppercase tracking-wider text-[#8a9bb8] mb-1 block">Monetization</label>
                  <select value={monetization} onChange={e => setMonetization(e.target.value)} className="w-full bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#d4af37]/50">
                    <option>One-time</option><option>Subscription</option><option>Freemium</option><option>Ads</option><option>Not sure</option>
                  </select>
                </div>
                <div><label className="text-[10px] uppercase tracking-wider text-[#8a9bb8] mb-1 block">Your Skills</label>
                  <div className="flex flex-wrap gap-2">
                    {skillOptions.map(s => (
                      <button key={s} onClick={() => setSkills(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                        className={`px-2.5 py-1 rounded-lg text-xs transition-colors ${skills.includes(s) ? 'bg-[#d4af37] text-[#0a1628] font-medium' : 'bg-[#132238] text-[#8a9bb8] border border-white/10 hover:bg-[#1a2d4a]'}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div><label className="text-[10px] uppercase tracking-wider text-[#8a9bb8] mb-1 block">Build Time Available</label>
                  <select value={buildTime} onChange={e => setBuildTime(e.target.value)} className="w-full bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#d4af37]/50">
                    <option>Weekend</option><option>2 weeks</option><option>1 month</option><option>3 months</option><option>6+ months</option>
                  </select>
                </div>
              </div>
              <button onClick={validate} className="w-full mt-4 px-4 py-2.5 rounded-lg bg-[#d4af37] text-[#0a1628] font-semibold text-sm hover:bg-[#e8c547] transition-colors flex items-center justify-center gap-1.5">
                <Target className="w-4 h-4"/> Validate Idea
              </button>
            </div>

            <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
              <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><Save className="w-4 h-4"/> Saved Ideas ({saved.length})</h2>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {saved.map(idea => (
                  <button key={idea.id} onClick={() => { setDescription(idea.description); setResult(idea.result); setBuild(idea.build); setCompetitors(idea.competitors); }}
                    className="w-full text-left bg-[#132238] rounded-lg p-2.5 border border-white/5 hover:border-[#d4af37]/30 transition-colors">
                    <div className="text-xs font-medium text-[#f0f0f0] line-clamp-2">{idea.description.slice(0, 80)}...</div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${verdictBg(idea.result.verdict)} ${verdictColor(idea.result.verdict)}`}>{idea.result.verdict}</span>
                      <span className="text-[10px] text-[#8a9bb8]">{idea.result.overallScore}/10 - {idea.build.estimatedWeeks}w</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section className="lg:col-span-7 space-y-4">
            {result && (
              <>
                <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-6 text-center">
                  <div className={`text-5xl font-bold mb-2 ${verdictColor(result.verdict)}`}>{result.overallScore}<span className="text-lg text-[#8a9bb8]">/10</span></div>
                  <div className={`inline-block px-4 py-1.5 rounded-lg text-sm font-semibold ${verdictBg(result.verdict)} ${verdictColor(result.verdict)}`}>{result.verdict}</div>
                  <div className="mt-6 space-y-3 text-left">
                    {Object.entries(result.dimensions).map(([dim, data]) => (
                      <div key={dim}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-[#f0f0f0] capitalize">{dim.replace('_', ' ')}</span>
                          <span className="text-xs text-[#8a9bb8]">{data.rawScore}/10 - Weight {data.weight}%</span>
                        </div>
                        <div className="h-2 bg-[#132238] rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(data.rawScore / 10) * 100}%`, backgroundColor: data.rawScore >= 7 ? '#22c55e' : data.rawScore >= 4 ? '#f59e0b' : '#ef4444' }}/>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {build && (
                  <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
                    <h3 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><Wrench className="w-4 h-4"/> Build Estimate</h3>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-[#070f1a] rounded-lg p-3 border border-white/10 text-center">
                        <div className="text-2xl font-bold text-[#f0f0f0]">{build.complexityScore}<span className="text-xs text-[#8a9bb8]">/10</span></div>
                        <div className="text-[10px] text-[#8a9bb8] uppercase tracking-wider mt-1">Complexity</div>
                      </div>
                      <div className="bg-[#070f1a] rounded-lg p-3 border border-white/10 text-center">
                        <div className="text-2xl font-bold text-[#f0f0f0]">{build.estimatedWeeks}<span className="text-xs text-[#8a9bb8]">w</span></div>
                        <div className="text-[10px] text-[#8a9bb8] uppercase tracking-wider mt-1">Est. Weeks</div>
                      </div>
                      <div className="bg-[#070f1a] rounded-lg p-3 border border-white/10 text-center">
                        <div className="text-2xl font-bold text-[#f0f0f0]">{build.skillMatch}<span className="text-xs text-[#8a9bb8]">%</span></div>
                        <div className="text-[10px] text-[#8a9bb8] uppercase tracking-wider mt-1">Skill Match</div>
                      </div>
                    </div>
                    <div className="mt-3 bg-[#070f1a] rounded-lg p-3 border border-[#d4af37]/20">
                      <div className="text-[10px] text-[#d4af37] uppercase tracking-wider mb-1">CatalystAgile Startup Idea Validator Prompt</div>
                      <p className="text-xs text-[#f0f0f0] font-mono leading-relaxed">{build.buildPrompt}</p>
                      <button onClick={() => navigator.clipboard.writeText(build.buildPrompt)} className="mt-2 text-[10px] text-[#8a9bb8] hover:text-[#d4af37] flex items-center gap-1 transition-colors"><Copy className="w-3 h-3"/> Copy Prompt</button>
                    </div>
                  </div>
                )}

                {competitors && (
                  <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
                    <h3 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><Swords className="w-4 h-4"/> Competitor Landscape</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {competitors.direct.map(c => <span key={c} className="px-2 py-1 rounded-lg bg-[#132238] border border-white/10 text-xs text-[#f0f0f0]">{c}</span>)}
                    </div>
                    <div className="space-y-2">
                      {competitors.angles.map((angle, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-[#f0f0f0]">
                          <ChevronRight className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 shrink-0"/>
                          {angle}
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 text-xs text-[#8a9bb8] italic">Tip: {competitors.tip}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-[#0f1d32] rounded-xl border border-green-500/20 p-4">
                    <h3 className="text-sm font-semibold text-green-400 mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4"/> Strengths</h3>
                    <div className="space-y-1.5">
                      {result.strengths.map((s, i) => <p key={i} className="text-xs text-[#f0f0f0]">- {s}</p>)}
                      {result.strengths.length === 0 && <p className="text-xs text-[#8a9bb8] italic">No strong dimensions identified.</p>}
                    </div>
                  </div>
                  <div className="bg-[#0f1d32] rounded-xl border border-red-500/20 p-4">
                    <h3 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4"/> Weaknesses</h3>
                    <div className="space-y-1.5">
                      {result.weaknesses.map((w, i) => <p key={i} className="text-xs text-[#f0f0f0]">- {w}</p>)}
                      {result.weaknesses.length === 0 && <p className="text-xs text-green-400 italic">No major weaknesses!</p>}
                    </div>
                  </div>
                </div>

                {result.suggestions.length > 0 && (
                  <div className="bg-[#0f1d32] rounded-xl border border-yellow-500/20 p-4">
                    <h3 className="text-sm font-semibold text-yellow-400 mb-2 flex items-center gap-2"><Lightbulb className="w-4 h-4"/> Suggestions</h3>
                    <div className="space-y-1.5">
                      {result.suggestions.map((s, i) => <p key={i} className="text-xs text-[#f0f0f0]">{i+1}. {s}</p>)}
                    </div>
                  </div>
                )}

                <div className="bg-[#0f1d32] rounded-xl border border-[#d4af37]/20 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-[#d4af37] flex items-center gap-2"><Sparkles className="w-4 h-4"/> AI Deep Analysis</h3>
                    <button onClick={handleAiDeep} disabled={isAiLoading} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-[#d4af37]/30 text-[#d4af37] transition-colors disabled:opacity-50">
                      {isAiLoading ? 'Analyzing...' : 'Generate'}
                    </button>
                  </div>
                  <div className="bg-[#070f1a] rounded-lg p-4 text-sm text-[#f0f0f0] whitespace-pre-wrap border border-white/10 min-h-[120px]">{aiAnalysis || 'Click Generate for AI-powered deep analysis: niche angles, revenue estimates, marketing channels.'}</div>
                </div>
              </>
            )}

            {!result && (
              <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-12 text-center">
                <Lightbulb className="w-12 h-12 text-[#8a9bb8] mx-auto mb-4"/>
                <h2 className="text-lg font-semibold text-[#f0f0f0] mb-2">Validate Your Idea</h2>
                <p className="text-sm text-[#8a9bb8]">Fill in the form and click Validate to score your idea across 5 dimensions.</p>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
